import React, { useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { ApolloError } from '@apollo/client';
import Paper from '@mui/material/Paper';
import { makeStyles } from '@mui/styles';
import { Theme } from '@mui/material/styles';
import Divider from '@mui/material/Divider';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Helmet from '../../../components/Helmet';
import CircularProgress from '../../../components/_loading/CircularProgress';
import Context from '../../../context';
import { useCompanyQuery } from '../../../../graphql/query/company/useCompanyQuery';
import NotFound from '../../../components/_error/NotFound';
import BusinessLayout from '../../../layout/Business';
import { ITranslationSubTypeInput } from '../../../../graphql/type/_inputTypes/translation';
import { useUpdateCompanyProfileMutation } from '../../../../graphql/mutation/company/updateCompanyProfileMutation';
import useForm from '../../../components/_hook/useForm';
import useNotification from '../../../components/_hook/useNotification';
import useLanguage from '../../../components/_hook/useLanguage';
import StickyFormFooter from '../../../components/_form/StickyFormFooter';
import SuperAdminPermission from '../../../adminComponents/_permission/SuperAdminPermission';
import InputTranslationTextField from '../../../components/_input/InputTranslation/InputTranslationTextField';
import SelectCountry from '../../../components/_select/SelectCountry';
import Select, {
  IValue,
  IValues,
} from '../../../components/_select/_module/Select';
import InputTranslationRichtext from '../../../components/_input/InputTranslation/InputTranslationRichtext';

const useStyles = makeStyles<Theme>((theme: Theme) => ({
  paper: {
    margin: theme.spacing(1),
    padding: theme.spacing(3),
    width: '100%',
  },
  contentCard: {
    width: '100%',
    backgroundColor: theme.palette.mode === 'light' ? '#f3f3f3' : 'inherit',
  },
}));

function BusinessProfile() {
  interface IFormValue {
    status: boolean;
    verification: boolean;
    active: boolean;
    name: ITranslationSubTypeInput[];
    aka: ITranslationSubTypeInput[];
    uen?: string;
    origin: string;
    slogan: ITranslationSubTypeInput[];
    profile: ITranslationSubTypeInput[];
    businessType: number[];
    incorporateDate?: number;
    workforceNumber?: number;
    annualRevenue?: number;
    salesRatioPercentage?: number;
    uploadedProfileImageIds?: string[];
  }

  const classes = useStyles();
  const { notification } = useNotification();
  const { t } = useTranslation();
  const { getText } = useLanguage();
  const { company, isSuperAdmin } = useContext(Context);
  const companyFragment = `
    _id
    uen
    active
    verification
    status
    origin
    slug
    aka {
        la
        text
    }
    name {
        la
        text
    }
    infos {
      slogan {
        la
        text
      }
      profile {
        la
        text
      }
      incorporate
      businesstype
      sales {
        ratio
        revenue
      }
      workforce
    }
  `;
  const { data, loading } = useCompanyQuery(companyFragment, {
    variables: {
      filter: {
        _id: company._id as string,
      },
      limit: 1,
    },
  });

  function onCompleted() {
    notification.success(t('done'));
  }

  function onError(e: ApolloError) {
    notification.error(e.message);
  }

  const [
    updateCompanyProfileMutation,
    { loading: isUpdatingCompanyProfile },
  ] = useUpdateCompanyProfileMutation(
    `updatedCompany {
				${companyFragment}
			}`,
    {
      onCompleted,
      onError,
    }
  );

  async function onSubmitForm(tempValues: IFormValue) {
    if (data) {
      await updateCompanyProfileMutation({
        variables: {
          _id: data.companies.items[0]._id,
          status: tempValues.status,
          verification: tempValues.verification,
          active: tempValues.active,

          name: tempValues.name,
          aka: tempValues.aka,
          uen: tempValues.uen,
          origin: tempValues.origin,

          slogan: tempValues.slogan,
          profile: tempValues.profile,
          businessType: tempValues.businessType,

          incorporateDate: tempValues.incorporateDate,
          workforceNumber: tempValues.workforceNumber,
          annualRevenue: tempValues.annualRevenue,
          salesRatioPercentage: tempValues.salesRatioPercentage,
          uploadedProfileImageIds: tempValues.uploadedProfileImageIds,
        },
      });
    }
  }

  const {
    values,
    setValue,
    handleSubmit,
    errors,
    setValues,
  } = useForm<IFormValue>({
    defaultValues: {
      status: false,
      verification: false,
      active: false,
      name: [],
      aka: [],
      uen: '',
      origin: '',
      slogan: [],
      profile: [],
      businessType: [],
      incorporateDate: undefined,
      workforceNumber: undefined,
      annualRevenue: undefined,
      salesRatioPercentage: undefined,
      uploadedProfileImageIds: [],
    },
    onSubmit: onSubmitForm,
    onValidate: (tempValues) => {
      const newErrors: Record<string, string> = {};

      if (!tempValues.name.length || !getText(tempValues.name)) {
        newErrors.name = t('__name__is_required', {
          name: t('name'),
        });
      }

      Object.keys(newErrors).forEach((newError) =>
        notification.error(newErrors[newError])
      );

      return newErrors;
    },
  });

  useEffect(() => {
    if (!loading && data && data.companies.items.length) {
      const tempCompany = data.companies.items[0];
      const companyInfos = tempCompany.infos || {};
      const companyInfoSales = companyInfos.sales || {};

      setValues({
        status: tempCompany.status,
        verification: tempCompany.verification,
        active: tempCompany.active,
        name: tempCompany.name.map((label) => ({
          text: label.text,
          la: label.la,
        })),
        aka: tempCompany.aka
          ? tempCompany.aka.map((label) => ({
              text: label.text,
              la: label.la,
            }))
          : [],
        uen: tempCompany.uen,
        origin: tempCompany.origin,
        slogan: companyInfos.slogan
          ? companyInfos.slogan.map((label) => ({
              text: label.text,
              la: label.la,
            }))
          : [],
        profile: companyInfos.profile
          ? companyInfos.profile.map((label) => ({
              text: label.text,
              la: label.la,
            }))
          : [],
        businessType: companyInfos.businesstype || [],
        incorporateDate: companyInfos.incorporate,
        workforceNumber: companyInfos.workforce,
        annualRevenue: Number(companyInfoSales.revenue),
        salesRatioPercentage: Number(companyInfoSales.ratio),
        uploadedProfileImageIds: [],
      });
    }
  }, [loading]);

  const queryCompany = data ? data.companies.items[0] : null;

  if (loading) return <CircularProgress center color="primary" />;
  if (!queryCompany) return <NotFound />;

  return (
    <Paper square className={classes.paper}>
      <Helmet title={`${t('profile')} | ${t('business')}`} />
      <Grid container spacing={2}>
        <Grid container justifyContent="space-between" spacing={1}>
          <Grid item>
            <Typography variant="h6">
              {t('update')} {getText(company.name)} {t('profile')}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Divider />
          </Grid>
        </Grid>
        <Grid container item xs={12} spacing={3}>
          <Grid item xs={12}>
            <FormGroup
              row
              style={{
                alignItems: 'flex-start',
              }}
            >
              <FormControlLabel
                control={
                  <Switch
                    checked={values.status}
                    onChange={() => setValue('status', !values.status)}
                    color="primary"
                  />
                }
                label={t('publish')}
              />
              <SuperAdminPermission editableChildren>
                <FormControlLabel
                  control={
                    <Switch
                      checked={values.verification}
                      onChange={() =>
                        setValue('verification', !values.verification)
                      }
                      color="primary"
                    />
                  }
                  label={t('verified')}
                />
              </SuperAdminPermission>
              <SuperAdminPermission editableChildren>
                <FormControlLabel
                  control={
                    <Switch
                      checked={values.active}
                      onChange={() => setValue('active', !values.active)}
                      color="primary"
                    />
                  }
                  label={t('active')}
                />
              </SuperAdminPermission>
            </FormGroup>
          </Grid>
          <Grid item xs={12}>
            {isSuperAdmin ? (
              <SuperAdminPermission editableChildren>
                <div>
                  <InputTranslationTextField
                    fullWidth
                    required
                    label={t('business_name')}
                    values={values.name}
                    onChange={(tempValues) => setValue('name', tempValues)}
                    error={Boolean(errors.name)}
                    helperText={errors.name}
                  />
                </div>
              </SuperAdminPermission>
            ) : (
              <InputTranslationTextField
                fullWidth
                required
                disabled
                label={t('business_name')}
                values={values.name}
                onChange={(tempValues) => setValue('name', tempValues)}
                error={Boolean(errors.name)}
                helperText={errors.name}
              />
            )}
          </Grid>
          <Grid item xs={12}>
            <InputTranslationTextField
              fullWidth
              label={t('also_known_as')}
              values={values.aka}
              onChange={(tempValues) => setValue('aka', tempValues)}
              error={Boolean(errors.aka)}
              helperText={errors.aka}
            />
          </Grid>
          <Grid item xs={12}>
            {isSuperAdmin ? (
              <SuperAdminPermission editableChildren>
                <TextField
                  fullWidth
                  label={t('business_registration_number')}
                  value={values.uen}
                  onChange={(e) => setValue('uen', e.target.value)}
                  error={Boolean(errors.uen)}
                  helperText={errors.uen}
                />
              </SuperAdminPermission>
            ) : (
              <TextField
                fullWidth
                disabled
                label={t('business_registration_number')}
                value={values.uen}
                onChange={(e) => setValue('uen', e.target.value)}
                error={Boolean(errors.uen)}
                helperText={errors.uen}
              />
            )}
          </Grid>
          <Grid item xs={12}>
            <SelectCountry<IValue>
              isClearable
              fullWidth
              required
              label={t('business_origin')}
              value={values.origin}
              queryVariables={{
                filter: {},
              }}
              onChange={(option) => {
                setValue('origin', option ? option.value : '');
              }}
              error={Boolean(errors.origin)}
              helperText={errors.origin}
            />
          </Grid>
          <Grid item xs={12}>
            <InputTranslationTextField
              fullWidth
              required
              label={t('slogan')}
              values={values.slogan}
              onChange={(tempValues) => setValue('slogan', tempValues)}
              error={Boolean(errors.slogan)}
              helperText={errors.slogan}
            />
          </Grid>
          <Grid item xs={12}>
            <InputTranslationRichtext
              label={t('business_profile')}
              values={values.profile}
              enableUploadImage
              info={t('business_profile_hint')}
              onUploadedImage={(uploadedImages) =>
                setValue(
                  'uploadedProfileImageIds',
                  uploadedImages.map((uploadedImage) => uploadedImage._id)
                )
              }
              onChange={(tempValues) => setValue('profile', tempValues)}
            />
          </Grid>
          <Grid item xs={12}>
            <Select<IValues>
              isClearable
              options={[
                {
                  label: t('manufacturer_producer'),
                  value: '1',
                },
                {
                  label: t('wholesaler'),
                  value: '2',
                },
                {
                  label: t('distributor'),
                  value: '3',
                },
                {
                  label: t('service_provider'),
                  value: '4',
                },
                {
                  label: t('retailer'),
                  value: '5',
                },
                {
                  label: t('agent_representative'),
                  value: '6',
                },
                {
                  label: t('subcontractor'),
                  value: '7',
                },
                {
                  label: t('government_ministry'),
                  value: '8',
                },
                {
                  label: t('association'),
                  value: '9',
                },
                {
                  label: t('non_profit_organisation'),
                  value: '10',
                },
              ]}
              isMulti
              label={t('business_type')}
              fullWidth
              menuPosition="fixed"
              value={values.businessType.map((type) => type.toString())}
              onChange={(options) =>
                setValue(
                  'businessType',
                  options.map((option) => Number(option.value))
                )
              }
              error={Boolean(errors.businessType)}
              helperText={errors.businessType}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              type="number"
              fullWidth
              placeholder="YYYYMMDD"
              inputProps={{ min: '0' }}
              label={t('date_incorporated')}
              value={values.incorporateDate}
              onChange={(e) =>
                setValue('incorporateDate', parseInt(e.target.value, 10))
              }
              error={Boolean(errors.incorporateDate)}
              helperText={errors.incorporateDate}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              type="number"
              fullWidth
              inputProps={{ min: '0' }}
              label={t('workforce')}
              value={values.workforceNumber}
              onChange={(e) =>
                setValue('workforceNumber', parseInt(e.target.value, 10))
              }
              error={Boolean(errors.workforceNumber)}
              helperText={errors.workforceNumber}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              type="number"
              fullWidth
              inputProps={{ min: '0', step: '1000' }}
              label={t('annual_revenue')}
              value={values.annualRevenue}
              onChange={(e) =>
                setValue('annualRevenue', parseFloat(e.target.value))
              }
              error={Boolean(errors.annualRevenue)}
              helperText={errors.annualRevenue}
              // eslint-disable-next-line react/jsx-no-duplicate-props
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">$</InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              type="number"
              fullWidth
              inputProps={{ min: '0', max: '100' }}
              label={t('sales_ratio')}
              value={values.salesRatioPercentage}
              onChange={(e) =>
                setValue('salesRatioPercentage', parseFloat(e.target.value))
              }
              error={Boolean(errors.salesRatioPercentage)}
              helperText={errors.salesRatioPercentage}
              // eslint-disable-next-line react/jsx-no-duplicate-props
              InputProps={{
                endAdornment: <InputAdornment position="end">%</InputAdornment>,
              }}
            />
          </Grid>
        </Grid>
        <StickyFormFooter
          onSubmit={handleSubmit}
          isSubmitting={isUpdatingCompanyProfile}
        />
      </Grid>
    </Paper>
  );
}

export default () => <BusinessLayout main={<BusinessProfile />} />;
